import { catchBackendApiClient } from '@/client/customFetcher';
import icons from '@/assets/icons';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { motion } from 'motion/react';
import { useEffect, useState } from 'react';
import '../../../../dashboard-reset.css';
import { LetterGenerationModal } from './LetterGenerationModal';
import { useLetterGeneration } from './hooks/useLetterGeneration';

const STORAGE_KEY_LETTER_ID = 'llm-last-letter-id';
const STORAGE_KEY_BADGE_DISMISSED = 'llm-letter-badge-dismissed';

export const LlmDoctorsNoteCallout = () => {
  const {
    callLetterToDoctorCTAClicked,
    callDownloadPDFClicked,
    callDownloadLetterClicked,
    callLetterGenerationStarted,
    callLetterGenerationRetry,
  } = useAnalyticsCalls();
  const {
    status,
    content,
    letter_id,
    error,
    startGeneration,
    retry,
    downloadPDF,
    reset,
    clearStatus,
    isGenerating,
    setLetterId,
    isDownloading,
  } = useLetterGeneration();

  const [showModal, setShowModal] = useState(false);
  const [showBadge, setShowBadge] = useState(true);
  const [hasExistingLetter, setHasExistingLetter] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Prevent double-clicks
  const [isCheckingLetter, setIsCheckingLetter] = useState(true); // Loading state for API check

  // Check for existing letter from API on mount (source of truth)
  useEffect(() => {
    const checkForExistingLetter = async () => {
      try {
        const response = await catchBackendApiClient.get<{ letter_id?: string; created_at?: string }>(
          '/v1/llm-context/letter/latest'
        );

        if (response.data.letter_id) {
          setHasExistingLetter(true);
          setLetterId(response.data.letter_id);
          // Also save to localStorage for badge dismissal tracking
          localStorage.setItem(STORAGE_KEY_LETTER_ID, response.data.letter_id);
        }
      } catch (err) {
        console.error('Failed to check for existing letter:', err);
      } finally {
        setIsCheckingLetter(false);
      }
    };

    checkForExistingLetter();

    // Load badge dismissal state from localStorage
    const badgeDismissed = localStorage.getItem(STORAGE_KEY_BADGE_DISMISSED) === 'true';
    setShowBadge(!badgeDismissed);
  }, [setLetterId]);

  // Save letter_id to localStorage when available
  useEffect(() => {
    if (letter_id) {
      localStorage.setItem(STORAGE_KEY_LETTER_ID, letter_id);
      setHasExistingLetter(true);
    }
  }, [letter_id]);

  // Open modal when generation starts
  useEffect(() => {
    if (isGenerating || status === 'validation_failed' || status === 'validation_passed' || status === 'error') {
      setShowModal(true);
    }
  }, [isGenerating, status]);

  const handleButtonClick = async () => {
    // Prevent double-clicks
    if (isLoading || isGenerating) {
      console.log('[LLM Letter] Button click ignored - already loading');
      return;
    }

    console.log('[LLM Letter] Button clicked', { hasExistingLetter, letter_id, status });
    setIsLoading(true);

    try {
      // Dismiss badge on first click
      if (showBadge) {
        setShowBadge(false);
        localStorage.setItem(STORAGE_KEY_BADGE_DISMISSED, 'true');
      }

      // Track analytics
      try {
        callLetterToDoctorCTAClicked();
      } catch (err) {
        console.warn('Analytics tracking failed:', err);
      }

      if (hasExistingLetter && letter_id) {
        // Direct download path for existing letter
        console.log('[LLM Letter] Taking download path for existing letter');

        try {
          callDownloadLetterClicked();
        } catch (err) {
          console.warn('Analytics tracking failed:', err);
        }

        try {
          await downloadPDF();
        } catch (err) {
          // Fallback to modal on download error
          console.error('Direct download failed, opening modal:', err);
          setShowModal(true);
        }
      } else {
        // Open modal and start generation
        console.log('[LLM Letter] Opening modal and starting generation');
        setShowModal(true);

        try {
          callLetterGenerationStarted();
        } catch (err) {
          console.warn('Analytics tracking failed:', err);
        }

        try {
          await startGeneration();
        } catch (err) {
          console.error('[LLM Letter] Generation failed:', err);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    // Use clearStatus instead of reset to preserve letter_id
    // This prevents modal from reopening when user clicks "Download my letter"
    clearStatus();
  };

  const handleRetry = async () => {
    try {
      callLetterGenerationRetry();
    } catch (err) {
      console.warn('Analytics tracking failed:', err);
    }
    await retry();
  };

  const handleDownload = async () => {
    if (letter_id) {
      try {
        callDownloadPDFClicked();
      } catch (err) {
        console.warn('Analytics tracking failed:', err);
      }
    }
    await downloadPDF();
  };

  // Dynamic button text
  const buttonText = hasExistingLetter ? 'Download my letter' : 'Generate Letter';

  // Dynamic subtext
  const subtext = hasExistingLetter
    ? 'Your letter is ready! Download it to share with your doctor.'
    : 'Get a personalized letter for your doctor summarizing your results and recommended next steps.';

  // Modal can close unless actively generating
  const canCloseModal = status !== 'streaming' && status !== 'validating';

  return (
    <>
      <div className="dashboard-reset w-full">
        {/* Outer wrapper with hero background */}
        <div className="relative w-full overflow-hidden rounded-lg bg-[#183344]">
          {/* Background Video - twilight sky with clouds */}
          <video
            src="/videos/twilight.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 z-0 h-full w-full object-cover"
          />

          {/* Dark overlay for better text contrast */}
          <div className="absolute left-0 top-0 z-[1] h-full w-full bg-black/70" />

          {/* Content card with frosted glass effect */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative z-10 px-6 py-6"
            style={{
              backdropFilter: 'blur(24px) saturate(120%)',
              WebkitBackdropFilter: 'blur(24px) saturate(120%)',
            }}
          >
            {/* Icon + Header + Badge Row */}
            <div className="mb-4 flex items-center gap-3">
              {/* Document Icon */}
              <img
                src={icons['Clinical-Dr']}
                alt=""
                className="h-8 w-8 opacity-80 brightness-0 invert"
                aria-hidden="true"
              />

              {/* Header */}
              <h3 className="text-xl font-bold text-white/90">Letter to Doctor</h3>

              {/* NEW Badge - dismissible */}
              {showBadge && (
                <span
                  className="rounded px-2 py-1 text-xs font-bold uppercase"
                  style={{ backgroundColor: '#5db89a', color: '#183344' }}
                >
                  New
                </span>
              )}
            </div>

            {/* Description - dynamic based on letter state */}
            <p className="mb-5 text-sm leading-relaxed text-gray-400">{subtext}</p>

            {/* CTA Button with Rainbow Border */}
            <div className="relative rounded-full p-[2px]">
              {/* Animated rainbow gradient background for button border */}
              <div
                className="absolute inset-0 rounded-full opacity-80"
                style={{
                  background: `linear-gradient(
                    90deg,
                    #ff0080 0%,
                    #ff4d94 12.5%,
                    #ff8c00 25%,
                    #ffd700 37.5%,
                    #00d4ff 50%,
                    #4da6ff 62.5%,
                    #7b68ee 75%,
                    #b857ff 87.5%,
                    #ff0080 100%
                  )`,
                  backgroundSize: '300% 100%',
                  animation: 'rainbow-shift 6s linear infinite',
                }}
              />

              {/* Button content - dynamic text */}
              <button
                onClick={handleButtonClick}
                disabled={isGenerating || isLoading || isCheckingLetter}
                className="relative z-10 w-full rounded-full bg-catch-dark-blue-90 px-6 py-3 text-base font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={
                  hasExistingLetter
                    ? 'Download your letter to share with your doctor'
                    : 'Generate an AI-powered letter to share with your doctor'
                }
              >
                {buttonText}
              </button>
            </div>
          </motion.div>
        </div>

        {/* Keyframe animation styles */}
        <style>{`
          @keyframes rainbow-shift {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }

          /* Respect user's motion preferences */
          @media (prefers-reduced-motion: reduce) {
            [style*="rainbow-shift"] {
              animation-duration: 10s !important;
            }
          }
        `}</style>
      </div>

      {/* Modal */}
      <LetterGenerationModal
        isOpen={showModal}
        status={status}
        content={content}
        error={error}
        onClose={handleModalClose}
        onRetry={handleRetry}
        onDownload={handleDownload}
        canClose={canCloseModal}
        isDownloading={isDownloading}
      />
    </>
  );
};
